import { useEffect, useMemo, useRef, useState } from 'react';
import { Application } from '@core/Application';
import { RosterAnalyticsHelper } from '@core/Analytics/RosterAnalyticsHelper';
import { RosterQontactPointStateFilter } from '@core/Analytics/RosterEvents';
import { Organization } from '@models/Organization';
import { RosterService, RosterServiceModel } from '@services/RosterService';
import { QontyEmptyState } from '@components/QontyEmptyState';
import { QontyLoading } from '@components/QontyLoading';
import { HorizontalContainer, VerticalContainer } from '@shared/designSystem/Containers';
import { DateTriggerButton, RefreshButton } from '@shared/designSystem/Button';
import { Modal } from '@shared/designSystem/messages/Modal';
import SearchBar from '@shared/designSystem/SearchBar';
import StatCard from '@shared/designSystem/StatCard';
import DSStatusBadge from '@shared/designSystem/StatusBadge';
import UserCard from '@shared/designSystem/UserCard';
import designSystemStyles from '@shared/styles/designSystemStyles';
import ApartmentRounded from '@mui/icons-material/ApartmentRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import PersonAddAltRounded from '@mui/icons-material/PersonAddAltRounded';
import TagRounded from '@mui/icons-material/TagRounded';
import { StatusBadge } from './RosterScreen';
import { rosterStatusColor, formatCheckinTime } from './RosterScreenViewModel';
import {
    QontactPointShiftView,
    QontactPointState,
    useQontactPointRosterScreenViewModel,
} from './QontactPointRosterScreenViewModel';
import LocalDateSelector from './LocalDateSelector';
import styles from './QontactPointRosterScreen.module.css';
import Utils from '@utils/Utils';

interface QontactPointRosterScreenProps {
    org: Organization;
}

const usersGridColumns = '1fr 160px 150px';

const QP_STATE_COLORS: Record<QontactPointState, string> = {
    inProgress: designSystemStyles.colorInfo,
    done: designSystemStyles.colorSuccess,
    notFulfilled: designSystemStyles.colorRejected,
    upcoming: designSystemStyles.colorInProgress,
};

const QP_STATE_LABELS: Record<QontactPointState, string> = {
    inProgress: 'En curso',
    done: 'Completado',
    notFulfilled: 'No cumplido',
    upcoming: 'Próximo',
};

const getQontactPointEmptyStateText = (
    hasRosterData: boolean,
    hasSearchQuery: boolean,
    hasStateFilter: boolean,
): string => {
    if (!hasRosterData) {
        return 'No hay Qontact Points con turnos para este día.'.localized();
    }
    if (hasSearchQuery) {
        return 'No se encontraron Qontact Points para esta búsqueda.'.localized();
    }
    if (hasStateFilter) {
        return 'No hay Qontact Points para el estado seleccionado.'.localized();
    }
    return 'No hay Qontact Points para mostrar.'.localized();
};

const QontactPointStateBadge: React.FC<{ state: QontactPointState }> = ({ state }) => {
    const color = QP_STATE_COLORS[state];
    return <DSStatusBadge label={QP_STATE_LABELS[state].localized()} color={color} />;
};

interface CheckinCellProps {
    user: RosterServiceModel.QontactPointShiftUser;
    timezone: string;
}

const CheckinCell: React.FC<CheckinCellProps> = ({ user, timezone }) => {
    if (user.actualCheckinTimestamp == null) {
        return (
            <div className={styles.cellRight}>
                <span className={`${styles.checkinLine} ${styles.checkinMuted}`}>
                    {`↑ ${'Sin fichar'.localized()}`}
                </span>
                <span className={`${styles.checkinLine} ${styles.checkinMuted}`}>
                    {`↓ ${'Sin fichar'.localized()}`}
                </span>
            </div>
        );
    }

    const checkin = formatCheckinTime(user.actualCheckinTimestamp, timezone);
    const checkout = user.actualCheckoutTimestamp != null
        ? formatCheckinTime(user.actualCheckoutTimestamp, timezone)
        : null;

    return (
        <div className={styles.cellRight}>
            <span className={styles.checkinLine}>{`↑ ${checkin}`}</span>
            {checkout != null
                ? <span className={styles.checkinLine}>{`↓ ${checkout}`}</span>
                : <span className={`${styles.checkinLine} ${styles.checkinMuted}`}>{`↓ ${'Sin fichar'.localized()}`}</span>}
        </div>
    );
};

interface ShiftBlockProps {
    shift: QontactPointShiftView;
    expanded: boolean;
    onToggle: () => void;
    timezone: string;
}

const ShiftBlock: React.FC<ShiftBlockProps> = ({ shift, expanded, onToggle, timezone }) => {
    const ratio = shift.totalCount > 0 ? shift.presentCount / shift.totalCount : 0;
    const progressColor = ratio >= 1 ? rosterStatusColor('PRESENTE') : rosterStatusColor('CON_DEMORA');
    const isAssigned = shift.mode === 'assigned';

    return (
        <div className={styles.shiftBlock}>
            <div className={styles.shiftHeader} onClick={onToggle}>
                <div className={styles.shiftHeaderLeft}>
                    <span className={styles.chevron}>
                        {expanded ? <ExpandMoreRounded fontSize="small" /> : <ChevronRightRounded fontSize="small" />}
                    </span>
                    <div className={styles.shiftTitleBlock}>
                        <p className={styles.shiftTitle}>{shift.name || '—'}</p>
                        <p className={styles.shiftTime}>{`${shift.hourFrom} – ${shift.hourTo}`}</p>
                    </div>
                </div>
                <div className={styles.shiftHeaderRight}>
                    <span className={styles.modeBadge}>
                        {isAssigned
                            ? <PersonAddAltRounded style={{ fontSize: 14 }} />
                            : <TagRounded style={{ fontSize: 14 }} />}
                        {isAssigned ? 'Asignados'.localized() : 'Conteo'.localized()}
                    </span>
                    <div className={styles.shiftProgressBlock}>
                        <span className={styles.shiftProgressText} style={{ color: progressColor }}>
                            {`${shift.presentCount}/${shift.totalCount}`}
                        </span>
                        <div className={styles.progressTrack}>
                            <div
                                className={styles.progressFill}
                                style={{ width: `${Math.min(ratio, 1) * 100}%`, backgroundColor: progressColor }}
                            />
                        </div>
                        <span className={styles.shiftProgressLabel}>{'presentes'.localized()}</span>
                    </div>
                </div>
            </div>

            {expanded && (
                shift.users.length === 0 ? (
                    <div className={styles.emptyShiftUsers}>
                        {isAssigned
                            ? 'No hay colaboradores asignados.'.localized()
                            : 'Nadie fichó en este turno.'.localized()}
                    </div>
                ) : (
                    <div className={styles.usersTable}>
                        <div className={styles.usersHeader} style={{ gridTemplateColumns: usersGridColumns }}>
                            <span className={styles.usersHeaderCell}>{'Colaborador'.localized()}</span>
                            <span className={styles.usersHeaderCell} style={{ textAlign: 'right' }}>{'Fichaje'.localized()}</span>
                            <span className={styles.usersHeaderCell} style={{ textAlign: 'right' }}>{'Estado'.localized()}</span>
                        </div>
                        {shift.users.map((user, index) => (
                            <div key={`${user.userID}-${index}`} className={styles.userRow} style={{ gridTemplateColumns: usersGridColumns }}>
                                <HorizontalContainer minWidth="0">
                                    <UserCard name={user.userName} imageURL={user.userImage} width="100%" />
                                </HorizontalContainer>
                                <CheckinCell user={user} timezone={timezone} />
                                <HorizontalContainer justifyContent="flex-end">
                                    <StatusBadge status={user.status} />
                                </HorizontalContainer>
                            </div>
                        ))}
                    </div>
                )
            )}
        </div>
    );
};

const QontactPointRosterScreen: React.FC<QontactPointRosterScreenProps> = ({ org }) => {
    const rosterService = useMemo(
        () => new RosterService(Application.shared().coreDependencies.httpClient),
        [],
    );
    const [isDateSelectorOpen, setIsDateSelectorOpen] = useState(false);
    const hasTrackedShownRef = useRef(false);
    const previousSearchQueryRef = useRef('');

    const viewModel = useQontactPointRosterScreenViewModel({ org, rosterService });
    const emptyStateText = getQontactPointEmptyStateText(
        viewModel.summary.total > 0,
        viewModel.searchQuery.trim().length > 0,
        viewModel.stateFilter != null,
    );

    useEffect(() => {
        if (hasTrackedShownRef.current) return;
        hasTrackedShownRef.current = true;
        RosterAnalyticsHelper.trackByQontactPointShown(viewModel.day);
    }, [viewModel.day]);

    useEffect(() => {
        const query = viewModel.searchQuery.trim();
        if (previousSearchQueryRef.current === query) return;

        const timeoutID = window.setTimeout(() => {
            previousSearchQueryRef.current = query;
            RosterAnalyticsHelper.trackByQontactPointSearchApplied({
                day: viewModel.day,
                queryLength: query.length,
                hasQuery: query.length > 0,
                resultCount: viewModel.qontactPoints.length,
                totalCount: viewModel.summary.total,
                activeFilter: viewModel.stateFilter ?? 'all',
            });
        }, 600);

        return () => window.clearTimeout(timeoutID);
    }, [viewModel.day, viewModel.qontactPoints.length, viewModel.searchQuery, viewModel.stateFilter, viewModel.summary.total]);

    const dayFromDate = (date: Date) => Utils.getDayStringFromTimestamp(date.getTime(), viewModel.timezone);

    const handleRefresh = () => {
        RosterAnalyticsHelper.trackByQontactPointRefreshed(viewModel.day);
        void viewModel.refresh();
    };

    const handleStateFilterSelected = (state: RosterQontactPointStateFilter) => {
        RosterAnalyticsHelper.trackByQontactPointStateFilterSelected(viewModel.day, state);
        viewModel.setStateFilter(state === 'all' ? null : state);
    };

    const handleQontactPointToggle = (qontactPointID: string) => {
        const expanded = !viewModel.isQontactPointExpanded(qontactPointID);
        RosterAnalyticsHelper.trackByQontactPointToggled(viewModel.day, qontactPointID, expanded);
        viewModel.toggleQontactPoint(qontactPointID);
    };

    const handleShiftToggle = (qontactPointID: string, shiftInstanceID: string) => {
        const expanded = !viewModel.isShiftExpanded(shiftInstanceID);
        RosterAnalyticsHelper.trackByQontactPointShiftToggled(viewModel.day, qontactPointID, shiftInstanceID, expanded);
        viewModel.toggleShift(shiftInstanceID);
    };

    const trackDateChangedIfNeeded = (day: string) => {
        if (day !== viewModel.day) {
            RosterAnalyticsHelper.trackByQontactPointDateChanged(day);
        }
    };

    const handleDateSelected = (date: Date) => {
        trackDateChangedIfNeeded(dayFromDate(date));
        viewModel.setSelectedDate(date);
        setIsDateSelectorOpen(false);
    };

    const handleDateCleared = () => {
        const today = new Date();
        trackDateChangedIfNeeded(dayFromDate(today));
        viewModel.setSelectedDate(today);
        setIsDateSelectorOpen(false);
    };

    const listContent = viewModel.loading ? (
        <QontyLoading text={'Cargando roster...'.localized()} withQonty={true} />
    ) : viewModel.qontactPoints.length === 0 ? (
        <div className={styles.emptyState}>
            <QontyEmptyState
                text={emptyStateText}
                darkMode={false}
            />
        </div>
    ) : (
        <div className={styles.listScroll}>
            {viewModel.qontactPoints.map((qontactPoint) => {
                const expanded = viewModel.isQontactPointExpanded(qontactPoint.qontactPointID);
                return (
                    <div key={qontactPoint.qontactPointID} className={styles.qpCard}>
                        <div className={styles.qpHeader} onClick={() => handleQontactPointToggle(qontactPoint.qontactPointID)}>
                            <div className={styles.qpHeaderLeft}>
                                <span
                                    className={styles.qpIcon}
                                    style={{
                                        color: QP_STATE_COLORS[qontactPoint.state],
                                        backgroundColor: `color-mix(in srgb, ${QP_STATE_COLORS[qontactPoint.state]} 10%, transparent)`,
                                    }}
                                >
                                    <ApartmentRounded fontSize="small" />
                                </span>
                                <div className={styles.qpTitleBlock}>
                                    <p className={styles.qpTitle}>{qontactPoint.buildingName || '—'}</p>
                                    <p className={styles.qpId}>{qontactPoint.qontactPointID}</p>
                                </div>
                            </div>
                            <div className={styles.qpHeaderRight}>
                                <QontactPointStateBadge state={qontactPoint.state} />
                                <span className={styles.qpPresentes}>
                                    {`${qontactPoint.presentCount}/${qontactPoint.totalCount} ${'presentes'.localized()}`}
                                </span>
                                <span className={styles.chevron}>
                                    {expanded ? <ExpandMoreRounded /> : <ChevronRightRounded />}
                                </span>
                            </div>
                        </div>

                        {expanded && (
                            <div className={styles.shiftsContainer}>
                                {qontactPoint.shifts.map((shift) => (
                                    <ShiftBlock
                                        key={shift.shiftInstanceID}
                                        shift={shift}
                                        expanded={viewModel.isShiftExpanded(shift.shiftInstanceID)}
                                        onToggle={() => handleShiftToggle(qontactPoint.qontactPointID, shift.shiftInstanceID)}
                                        timezone={viewModel.timezone}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );

    return (
        <VerticalContainer
            className={styles['roster-content']}
            width="100%"
            height="100%"
            minHeight="0"
            gap={designSystemStyles.gapLg}
        >
            <HorizontalContainer
                width="100%"
                gap={designSystemStyles.gapMd}
                alignment="center"
                justifyContent="space-between"
                flexWrap="wrap"
                flexShrink={0}
            >
                <HorizontalContainer
                    width="100%"
                    gap={designSystemStyles.gapMd}
                    alignment="center"
                    flexShrink={0}
                >
                    <SearchBar
                        value={viewModel.searchQuery}
                        onChange={viewModel.setSearchQuery}
                        placeholder={'Buscar Qontact Point...'.localized()}
                        width="1020px"
                    />
                    <RefreshButton onClick={handleRefresh} />
                    <HorizontalContainer
                        alignment="flex-end"
                        justifyContent="flex-end"
                        width="100%"
                    >
                        <DateTriggerButton
                            text={viewModel.formattedDay}
                            onClick={() => setIsDateSelectorOpen(true)}
                        />
                    </HorizontalContainer>
                </HorizontalContainer>
                <HorizontalContainer
                    width="auto"
                    gap={designSystemStyles.gapMd}
                    alignment="center"
                    flexWrap="wrap"
                    minWidth="0"
                >
                    <StatCard label={'Qontact Points'.localized()} count={viewModel.summary.total} loading={viewModel.loading} color={designSystemStyles.colorTextSecondary} selected={viewModel.stateFilter === null} onClick={() => handleStateFilterSelected('all')} />
                    <StatCard label={'En curso'.localized()} count={viewModel.summary.inProgress} loading={viewModel.loading} color={QP_STATE_COLORS.inProgress} selected={viewModel.stateFilter === 'inProgress'} onClick={() => handleStateFilterSelected(viewModel.stateFilter === 'inProgress' ? 'all' : 'inProgress')} />
                    <StatCard label={'Completados'.localized()} count={viewModel.summary.done} loading={viewModel.loading} color={QP_STATE_COLORS.done} selected={viewModel.stateFilter === 'done'} onClick={() => handleStateFilterSelected(viewModel.stateFilter === 'done' ? 'all' : 'done')} />
                    <StatCard label={'No cumplidos'.localized()} count={viewModel.summary.notFulfilled} loading={viewModel.loading} color={QP_STATE_COLORS.notFulfilled} selected={viewModel.stateFilter === 'notFulfilled'} onClick={() => handleStateFilterSelected(viewModel.stateFilter === 'notFulfilled' ? 'all' : 'notFulfilled')} />
                </HorizontalContainer>
            </HorizontalContainer>

            {/* Nested list */}
            {listContent}

            {/* Date picker modal */}
            <Modal
                isOpen={isDateSelectorOpen}
                onClose={() => setIsDateSelectorOpen(false)}
                title={'Seleccionar fecha'.localized()}
                width="420px"
                maxWidth="92vw"
            >
                <LocalDateSelector
                    initialDate={viewModel.selectedDate}
                    events={{
                        onApply: (date) => {
                            handleDateSelected(date ?? new Date());
                        },
                        onClear: handleDateCleared,
                    }}
                />
            </Modal>
        </VerticalContainer>
    );
};

export default QontactPointRosterScreen;
